import React, { useState } from 'react';
import { Card, CardContent, Grid, Typography, Button, Link, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import TabUnselectedOutlinedIcon from '@mui/icons-material/TabUnselectedOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RechargePlans from '../../components/RechargePlans';
import API from '../../api/api';
import { startTour } from '../../components/OnboardingTour';
import { solutionsArticleUrl } from '../../config/solutionsArticles';

const styles = {
  card: (theme) => ({
    margin: '24px 0 36px 0',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: theme.palette.mode === 'dark' ? '#1c1a2e' : '#f7f6ff',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 2px 12px rgba(0,0,0,0.5), 0 1px 4px rgba(0,0,0,0.4)'
      : '0 2px 12px rgba(58, 32, 205, 0.1), 0 1px 4px rgba(0,0,0,0.06)',
  }),
  closeBtn: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    color: 'text.secondary',
    border: '1px solid',
    borderColor: 'divider',
    '&:hover': { color: 'text.primary', borderColor: 'text.secondary' },
  },
  cardContent: {
    padding: '36px 32px 28px',
  },
  title: {
    color: 'primary.main',
    fontWeight: 700,
    letterSpacing: '-0.5px',
  },
  subtitle: {
    color: 'text.secondary',
    fontWeight: 400,
    marginTop: '4px',
  },
  gridContainer: (theme) => ({
    padding: '0 8%',
    marginTop: '8px',
    [theme.breakpoints.up('lg')]: {
      padding: '0 12%',
    },
  }),
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px 12px',
  },
  iconWrapper: (theme) => ({
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(139, 116, 240, 0.16)'
      : 'rgba(58, 32, 205, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '12px',
    '& svg': {
      fontSize: '1.6rem',
      color: theme.palette.primary.main,
    },
  }),
  stepTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: 'text.primary',
    marginBottom: '4px',
  },
  stepLink: {
    fontSize: '0.8rem',
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '28px',
  },
  footer: {
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid',
    borderTopColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    color: 'text.secondary',
    fontSize: '0.875rem',
  },
  btnIcon: {
    marginRight: '8px',
  },
  videoWrapper: (theme) => ({
    marginTop: '28px',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0,0,0,0.5)'
      : '0 2px 8px rgba(0,0,0,0.12)',
    maxWidth: '720px',
    margin: '28px auto 0',
  }),
  video: {
    display: 'block',
    width: '100%',
    borderRadius: '8px',
  },
};

const steps = [
  { icon: <PublicOutlinedIcon />, key: 'step1', href: solutionsArticleUrl('manageLanguages') },
  { icon: <TranslateOutlinedIcon />, key: 'step2', href: solutionsArticleUrl('translatingContent') },
  { icon: <TabUnselectedOutlinedIcon />, key: 'step3', href: solutionsArticleUrl('switcherConfigurator') },
  { icon: <LanguageOutlinedIcon />, key: 'step4', href: solutionsArticleUrl('assignLanguagesToDomains') },
];

const InlineVideo = () => (
  <Box sx={styles.videoWrapper}>
    <video controls poster="/assets/onboarding-poster.jpg" sx={styles.video} style={{ display: 'block', width: '100%', borderRadius: '8px' }}>
      <source src="https://f003.backblazeb2.com/file/ly-help-center-public-files/langify+onboarding.mp4" type="video/mp4" />
    </video>
  </Box>
);

const WelcomeTile = (props) => {
  const { t } = useTranslation();
  const [showRechargePlans, setShowRechargePlans] = useState(false);

  const onBuyWords = (plan) => {
    API.buyWords(plan, () => {});
  };

  return (
    <Card sx={styles.card}>
      <Button sx={styles.closeBtn} size="small" onClick={props.onClose} endIcon={<CloseIcon />}>
        {t('onboarding.welcomeTile.close')}
      </Button>
      <CardContent sx={styles.cardContent}>
        <Typography variant="h4" align="center" gutterBottom sx={styles.title}>
          {t('onboarding.welcomeTile.title')}
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom sx={styles.subtitle}>
          {t('onboarding.welcomeTile.subtitle')}
        </Typography>

        <Grid container sx={styles.gridContainer}>
          {steps.map(({ icon, key, href }) => (
            <Grid key={key} size={{ xs: 6, md: 3 }} sx={styles.gridItem}>
              <Box sx={styles.iconWrapper}>{icon}</Box>
              <Typography align="center" sx={styles.stepTitle}>
                {t(`onboarding.welcomeTile.${key}`)}
              </Typography>
              <Typography variant="body2" align="center" sx={styles.stepLink}>
                <Link href={href} underline="always" target="_blank" color="primary">
                  {t('onboarding.welcomeTile.link')}
                </Link>
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <Button onClick={() => startTour()} variant="contained" color="primary" size="large">
            <MapOutlinedIcon sx={styles.btnIcon} /> {t('onboarding.welcomeTile.startTour')}
          </Button>
        </Box>

        <InlineVideo />

        <Box sx={styles.footer}>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('onboarding.welcomeTile.footerText')}{' '}
            <Link onClick={() => setShowRechargePlans(true)} underline="always" color="primary" sx={{ cursor: 'pointer' }}>
              {t('onboarding.welcomeTile.pricingLink')}
            </Link>
          </Typography>
        </Box>

        <RechargePlans
          visible={showRechargePlans}
          onClose={() => setShowRechargePlans(false)}
          onBuy={onBuyWords}
        />
      </CardContent>
    </Card>
  );
};

export default WelcomeTile;
